import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, Package, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

export default function Hero() {
  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-[85vh] flex items-center overflow-hidden bg-[#faf7f2]">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232d2926' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 py-20 md:py-28">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl">
          {/* New Minds badge */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Link to="/new-minds" className="inline-flex items-center gap-2 bg-[#c4683c]/10 border border-[#c4683c]/20 text-[#c4683c] px-4 py-1.5 rounded-full text-xs font-bold hover:bg-[#c4683c]/15 transition-all mb-6">
              New Minds — AI & Digital Coaching for Organizations <ArrowRight size={12} />
            </Link>
          </motion.div>

          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="text-sm font-bold uppercase tracking-[0.12em] text-[#2a9d8f] mb-5">
            AI Studio for Products, Consulting & Coaching — Built on Real-World Expertise
          </motion.p>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#2d2926] leading-[1.1]" style={{ fontFamily: 'Fraunces, serif' }}>
            AI is the tool.{" "}
            <span className="gradient-text">Domain expertise is the Moat.</span>
          </h1>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            className="text-base md:text-lg text-[#5c564e] leading-relaxed mt-6 max-w-2xl">
            BharatIntelliAI (Rewat IntelliForge Technologies) builds focused AI products for Indian businesses and professionals — grounded in 20+ years of domain experience across digital transformation, sales & marketing, telecom, supply chain, and enterprise operations.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-3 mt-10">
            <a href="#contact" data-testid="hero-cta-primary"
              className="inline-flex items-center justify-center gap-2 bg-[#2a9d8f] text-white px-7 py-3.5 rounded-full font-bold hover:bg-[#238b7f] transition-all duration-300 group">
              Book Free AI Assessment <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <div className="relative group inline-flex">
              <button
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 text-[#2d2926] px-7 py-3.5 rounded-full font-bold border border-[#d5cec5] hover:border-[#2a9d8f] hover:text-[#2a9d8f] transition-all duration-300"
              >
                Explore Our Offerings <ChevronDown size={16} />
              </button>
              <div className="absolute top-full left-0 mt-2 w-72 bg-white border border-[#ebe5dc] rounded-2xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 overflow-hidden">
                <a href="#products" className="flex items-start gap-3 px-5 py-4 hover:bg-[#f5f0e8] transition-colors">
                  <div className="w-8 h-8 bg-[#2a9d8f]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Package size={15} className="text-[#2a9d8f]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#2d2926]">Products</p>
                    <p className="text-xs text-[#8a8078] mt-0.5">HumanInLoop, Capex Intelligence, RebateIQ</p>
                  </div>
                </a>
                <div className="border-t border-[#ebe5dc]" />
                <Link to="/new-minds" className="flex items-start gap-3 px-5 py-4 hover:bg-[#f5f0e8] transition-colors">
                  <div className="w-8 h-8 bg-[#c4683c]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Sparkles size={15} className="text-[#c4683c]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#2d2926]">New Minds — AI & Digital Coaching</p>
                    <p className="text-xs text-[#8a8078] mt-0.5">For corporates, universities & organizations</p>
                  </div>
                </Link>
              </div>
            </div>
            <a href="#consulting" data-testid="hero-cta-consulting"
              className="inline-flex items-center justify-center gap-2 text-[#2d2926] px-7 py-3.5 rounded-full font-bold border border-[#d5cec5] hover:border-[#c4683c] hover:text-[#c4683c] transition-all duration-300">
              Our Consulting Services
            </a>
          </motion.div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} className="flex items-center gap-10 mt-16">
          {[
            { num: "90 Days", label: "To First ROI" },
            { num: "20+", label: "Years Expertise" },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-[#2d2926]" style={{ fontFamily: 'Fraunces, serif' }}>{stat.num}</p>
              <p className="text-xs text-[#8a8078] mt-1">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.a href="#vision" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#b8afa4] hover:text-[#2a9d8f] transition-colors animate-bounce" data-testid="scroll-indicator">
        <ChevronDown size={28} />
      </motion.a>
    </section>
  );
}
